import {CtaBtn} from "@/app/components/common/cta-btn/CtaBtn";
import {Popover, PopoverContent, PopoverTrigger} from "@/app/components/common/app-popover/AppPopover";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {ChevronDownSvg} from "@/app/components/icons/ChevronDownSvg";
import {cn} from "@/lib/utils";
import {Avatar, AvatarFallback, AvatarImage} from "@/app/components/common/app-avatar/AppAvatar";
import BalancePopoverContent from "@/app/components/common/app-header/BalancePopoverContent";
import AvatarPopoverContent from "@/app/components/common/app-header/AvatarPopoverContent";
import {WalletTabsEnum} from "@/lib/enums/WalletTabsEnum";
import type {Client} from "@stomp/stompjs";
import {profileApi, useGetBalanceQuery} from "@/lib/api/wallet/profileApi";
import {mapSocketBalance} from "@/lib/api/wallet/mappers";
import {formatMoney, getCurrencyFractionDigits} from "@/lib/utils/money/formatMoney";
import {useLocale} from "next-intl";
import Cookies from "js-cookie";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import {useTranslations} from "next-intl";
import Decimal from "decimal.js";
import {INewBalanceMessage} from "@/lib/types/wallet/INewBalanceMessage";
import {BalanceUpdateReasonsEnum} from "@/lib/enums/BalanceUpdateReasonsEnum";
import {api} from "@/lib/api/wallet/api";
import {useAppDispatch, useAppSelector} from "@/lib/hooks";
import {Skeleton} from "@/app/components/common/app-skeleton/AppSkeleton";
import CountUp from "react-countup";
import {useOpenDialog, useCloseDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {trackProfileOpened} from "@/lib/analytics/mixpanel-events";
import {setIsFundsCredited} from "@/lib/slices/uiSlice";
import {useGetBonusesQuery} from "@/lib/api/wallet/bonusApi";

export default function AuthorizedHeader() {
    const userId = useAppSelector(state => state.auth.userId)!;
    const currency = useAppSelector(state => state.cashier.userCurrency);
    const isFundsCredited = useAppSelector(state => state.ui.isFundsCredited);
    const t = useTranslations();
    const [isBalancePopoverOpen, setIsBalancePopoverOpen] = useState(false);
    const [isAvatarPopoverOpen, setIsAvatarPopoverOpen] = useState(false);
    const openDialog = useOpenDialog();
    const closeDialog = useCloseDialog();
    const fundsCreditedRef = useRef<string | null>(null);
    const {data: balance, isLoading: balanceFetching, refetch: refetchBalance} = useGetBalanceQuery();
    const {refetch: refetchBonuses} = useGetBonusesQuery();
    const dispatch = useAppDispatch();
    const [isBalanceUpdated, setIsBalanceUpdated] = useState(false);

    const locale = useLocale();

    const userBalance = useMemo(() => {
        // Stale-cookie guard: a currency the backend no longer returns falls
        // back to the USD row rather than an empty header.
        return balance?.find(b => b.currency === (currency || 'USD'))
            ?? balance?.find(b => b.currency === 'USD');
    }, [balance, currency]);

    const balanceCurrency = useMemo(() => {
        return userBalance?.currency || 'USD';
    }, [userBalance?.currency]);

    const balanceFractionDigits = useMemo(() => {
        return getCurrencyFractionDigits(locale, balanceCurrency);
    }, [locale, balanceCurrency]);

    // Truncated DOWN before display — never show money the player doesn't have.
    const balanceValue = useMemo(() => {
        return new Decimal(userBalance?.balance || 0)
            .toDP(balanceFractionDigits, Decimal.ROUND_DOWN)
            .toNumber();
    }, [userBalance?.balance, balanceFractionDigits]);

    const formatBalance = useMemo(() => {
        return (value: number) => formatMoney(value, balanceCurrency, locale);
    }, [balanceCurrency, locale]);

    const onWalletClicked = () => {
        openDialog(DialogsEnum.WALLET, {props: {tab: WalletTabsEnum.DEPOSIT, openSource: "header"}});
        setIsBalancePopoverOpen(false);
    }

    const onLogOutClicked = () => {
        openDialog(DialogsEnum.LOG_OUT);
        setIsAvatarPopoverOpen(false);
    }

    // The avatar popover is the app's profile surface — there is no profile route,
    // so this is what `profile_opened` (spec §15) can attach to.
    const onAvatarPopoverOpenChange = (open: boolean) => {
        setIsAvatarPopoverOpen(open);
        if (open) trackProfileOpened();
    }

    // FUNDS_CREDITED is driven by the redux flag (set by the deposit websocket).
    // It also closes from *inside* (the "Play" button flips the flag), so keep the
    // engine instance in sync with the flag in both directions.
    useEffect(() => {
        if (isFundsCredited && !fundsCreditedRef.current) {
            const {instanceId, result} = openDialog(DialogsEnum.FUNDS_CREDITED);
            fundsCreditedRef.current = instanceId;
            result.then(() => {
                fundsCreditedRef.current = null;
                dispatch(setIsFundsCredited(false));
            });
        } else if (!isFundsCredited && fundsCreditedRef.current) {
            closeDialog(fundsCreditedRef.current);
        }
    }, [isFundsCredited, openDialog, closeDialog, dispatch]);

    const onGoToBonusesClick = () => {
        setIsBalancePopoverOpen(false);
    }

    const clientRef = useRef<Client | null>(null);

    // sockjs-client + @stomp/stompjs are loaded on demand here rather than as
    // static imports — the balance socket isn't needed for LCP, and every
    // authenticated page previously paid for them at first paint (AC-803).
    useEffect(() => {
        if (clientRef.current) return;
        let cancelled = false;

        (async () => {
            const [{default: SockJS}, {Client}] = await Promise.all([
                import("sockjs-client"),
                import("@stomp/stompjs"),
            ]);
            if (cancelled || clientRef.current) return;

            clientRef.current = new Client({
                webSocketFactory: () => new SockJS(`${process.env.NEXT_PUBLIC_SOCKETS_URL + '/ws'}`),
                // debug: (msg) => msg,
                debug: (msg) => msg,
                reconnectDelay: 4000,
                connectHeaders: {
                    Authorization: `Bearer ${Cookies.get('accessToken')}`
                },
                onConnect: () => {
                    clientRef.current?.subscribe(`/topic/${userId}/balance`, function (notification) {
                        const msg: INewBalanceMessage = JSON.parse(notification.body);
                        if (msg.reason === BalanceUpdateReasonsEnum.DEPOSIT) {
                            dispatch(setIsFundsCredited(true));
                        }
                        // AC-798: map the snake_case/string WS payload into typed
                        // IBalance entries, carrying over the per-currency rate
                        // from the previous snapshot (the push has none).
                        dispatch(profileApi.util.updateQueryData('getBalance', undefined, (draft) =>
                            msg.balances.map(b =>
                                mapSocketBalance(b, draft?.find(d => d.currency === b.currency)))));
                        setIsBalanceUpdated(true);
                    });

                    clientRef.current?.subscribe(`/topic/${userId}/bonuses`, function () {
                        // A WS bonus push can carry a stale snapshot and arrive out of
                        // order relative to the activate-mutation refetch. Overwriting the
                        // cache with the payload lets a stale push clobber fresher data
                        // (e.g. a just-burned bonus flips back to "available" until reload).
                        // Invalidate instead so the authoritative GET is the single source
                        // of truth; this also refreshes the ladder view.
                        dispatch(api.util.invalidateTags(['GetBonuses', 'GetLadderCampaigns']));
                    });
                },
            })

            if (process.env.NEXT_PUBLIC_APP_ENV !== 'local') {
                clientRef.current.activate();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch, refetchBalance, refetchBonuses, userId]);

    return (
        <>
            <div className={'h-full flex gap-2 md:gap-3'}>
                <div className={'flex items-center gap-2 md:gap-3 h-full'}>
                    <Popover open={isBalancePopoverOpen} onOpenChange={setIsBalancePopoverOpen}>
                        {balance !== undefined ? (
                            <PopoverTrigger className={'h-full'} asChild>
                                <CtaBtn
                                    className={cn('px-4 py-2.5 h-full md:text-[16px] opacity-100 text-noble-black-300', {['bg-black text-white']: isBalancePopoverOpen})}>

                                        <span className={'flex items-center justify-between gap-[5px]'}>
                                            <span
                                                className={'text-nowrap'}>
                                                {!isBalanceUpdated ? (
                                                    <CountUp end={balanceValue} duration={0}
                                                             decimals={balanceFractionDigits}
                                                             formattingFn={formatBalance}/>
                                                ) : (
                                                    <CountUp preserveValue={true} end={balanceValue}
                                                             duration={2} decimals={balanceFractionDigits}
                                                             formattingFn={formatBalance}/>
                                                )}
                                            </span>
                                            <ChevronDownSvg
                                                className={cn('transition-transform duration-200 ease-in-out', {['rotate-180']: isBalancePopoverOpen})}
                                                width={15} height={15}/>
                                        </span>
                                </CtaBtn>
                            </PopoverTrigger>
                        ) : null}
                        <PopoverContent className={'p-4'}>
                            <BalancePopoverContent onGoToBonusesClick={onGoToBonusesClick} balances={balance || []}
                                                   onWalletClick={onWalletClicked}/>
                        </PopoverContent>
                    </Popover>

                    <MainCtaBtn
                        className={cn('h-full w-[125px] hidden md:block text-[16px]', {['pointer-events-none']: balanceFetching})}
                        onClick={() => openDialog(DialogsEnum.WALLET, {props: {tab: WalletTabsEnum.DEPOSIT, openSource: "header"}})}>{t('AuthorizedHeader.topUp')}</MainCtaBtn>
                </div>

                <Popover open={isAvatarPopoverOpen} onOpenChange={onAvatarPopoverOpenChange}>
                    <PopoverTrigger asChild>
                        <Avatar aria-controls={'user-avatar'}
                                className={'cursor-pointer w-9 h-9 md:w-[52px] md:h-full'}>
                            <AvatarImage fetchPriority={'high'} className={'w-[36px] h-[36px] md:w-[52px] md:h-full'}
                                         src="/avatar.png"
                                         alt="avatar"/>
                            <AvatarFallback className={'bg-dark-gray'}>
                                <Skeleton
                                    className={'w-full h-full bg-noble-black-600'}
                                />
                            </AvatarFallback>
                        </Avatar>
                    </PopoverTrigger>
                    <PopoverContent className={'max-w-[225px] p-0'}>
                        <AvatarPopoverContent onLogOutClick={onLogOutClicked}/>
                    </PopoverContent>
                </Popover>
            </div>

        </>
    )
}
